import dayjs from 'dayjs'
import { Array, Record } from 'effect'
import { pipe } from 'effect/Function'
import { isNotNullish } from 'effect/Predicate'
import z from 'zod'

import { timeframeSchema, type ViewTimeframe } from '../../../schemas/misc/timeframe.ts'
import { calcRelativeDate } from '../../util/date/calcRelativeDate.ts'

export function extractAndBuildRelativeMustacheParameters(
  rawSql: string,
  accountClosingMonth: number,
): Record<string, string> {
  // eslint-disable-next-line prefer-named-capture-group,require-unicode-regexp
  const regex = /relative__(?<fieldType>\w+)__(?<offset>[+-]?\d+)__(?<timeframe>\w+)__(?<direction>(from|to))/g
  const matches: RegExpMatchArray[] = [...rawSql.matchAll(regex)]

  return pipe(
    matches,
    Array.map((match) => {
      const [parameterName] = match
      const parsedParameterOptions = parseParameterOptions(match)

      if (parsedParameterOptions === undefined) {
        return null
      }

      const { fieldType, offset, timeframe, direction } = parsedParameterOptions
      const { startAt, endAt } = calcRelativeDate({
        now: dayjs(),
        offset,
        timeframe,
        dateFormat: fieldType === 'date' ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss',
        accountClosingMonth,
      })

      return {
        parameterName,
        value: direction === 'from' ? startAt : endAt,
      }
    }),
    Array.filter(isNotNullish),
    Array.map((x) => [x.parameterName, x.value] as const),
    Record.fromEntries,
  )
}

interface ParameterOptions {
  fieldType: 'date' | 'datetime'
  offset: number
  timeframe: ViewTimeframe
  direction: 'from' | 'to'
}

const fieldTypeSchema = z.enum(['date', 'datetime'])
const directionSchema = z.enum(['from', 'to'])

function parseParameterOptions(match: RegExpMatchArray): ParameterOptions | undefined {
  const fieldType = match.groups?.fieldType
  const offset = match.groups?.offset
  const timeframe = match.groups?.timeframe
  const direction = match.groups?.direction

  if (fieldType === undefined || offset === undefined || timeframe === undefined || direction === undefined) {
    return
  }

  const parsedFieldType = fieldTypeSchema.safeParse(fieldType)
  if (!parsedFieldType.success) {
    return
  }

  const parsedOffset = Number.parseInt(offset, 10)
  if (Number.isNaN(parsedOffset)) {
    return
  }

  const parsedTimeframe = timeframeSchema.safeParse(timeframe)
  if (!parsedTimeframe.success) {
    return
  }

  const parsedDirection = directionSchema.safeParse(direction)
  if (!parsedDirection.success) {
    return
  }

  return {
    fieldType: parsedFieldType.data,
    offset: parsedOffset,
    timeframe: parsedTimeframe.data,
    direction: parsedDirection.data,
  }
}
